<template>
    <div class="redesign-export-manage">
        <div class="flex flex-col gap-3">
            <div class="w-full">
                <VcInput
                    v-model="searchColumnInput"
                    prefix-icon="Search"
                    :placeholder="t('general.search')"
                />
            </div>
            <div class="w-auto flex-shrink-0">
                <div class="column-item">
                    <VcCheckbox
                        :model-value="isAllCheckboxChecked"
                        :indeterminate="isAllIndeterminate"
                        class="text-sm"
                        @update:modelValue="selectAllColumns"
                    >
                        {{ t('common.showAll') }}
                    </VcCheckbox>
                </div>
            </div>
        </div>
        <div
            class="export-manage px-4 mb-4 py-4"
        >
            <div v-if="hiddenSearchedColumns.length">
                <div
                    v-for="(column, i) in hiddenSearchedColumns"
                    :key="column.prop + i"
                    class="column-item column-item--active"
                >
                    <VcCheckbox
                        class="text-sm"
                        :model-value="false"
                        @change="addToVisible(column)"
                    >
                        {{ column.label.fullTextLabel || column.label?.toString() }}
                    </VcCheckbox>
                </div>
            </div>
            <draggable
                v-model="visibleDraggableColumns"
                @end="onEnd"
            >
                <div
                    v-for="(column, i) in visibleSearched"
                    :key="column.prop + 'column' + i"
                    :class="{'column-item--active' : column.highlight }"
                    class="column-item"
                >
                    <VcCheckbox
                        :model-value="true"
                        @change="addToHidden(column)"
                    >
                        {{ column.label.fullTextLabel || column.label?.toString() }}
                    </VcCheckbox>
                    <redesign-base-icon :name="'GripVertical'" />
                </div>
            </draggable>
            <div v-if="hiddenUnSearchColumns">
                <div
                    v-for="(column, i) in hiddenUnSearchColumns"
                    :key="column.prop + i"
                    class="column-item"
                >
                    <VcCheckbox
                        :model-value="false"
                        @change="addToVisible(column)"
                    >
                        {{ column.label.fullTextLabel || column.label?.toString() }}
                    </VcCheckbox>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { VueDraggableNext as draggable } from 'vue-draggable-next'
import { VcInput, VcCheckbox } from '@voicenter-team/voicenter-ui-plus'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { ColumnsToExport, WidgetTemplateColumnWithConfig } from '@/types/dashboard'

const { t } = useI18n()
const searchColumnInput = ref('')

const props = defineProps<{
    visibleColumns: WidgetTemplateColumnWithConfig[]
    hiddenColumns: WidgetTemplateColumnWithConfig[]
    modelValue: ColumnsToExport
}>()

const emit = defineEmits<{
    (event: 'update:modelValue', payload: ColumnsToExport): void;
}>()

const visibleDraggableColumns = ref<WidgetTemplateColumnWithConfig[]>()

function emitUpdate (data: ColumnsToExport) {
    emit('update:modelValue', data)
}

const isAllIndeterminate = computed(() => {
    return (props.visibleColumns.length > 0 && props.hiddenColumns.length > 0)
})
const isAllCheckboxChecked = computed(() => {
    return props.visibleColumns.length > 0 && !isAllIndeterminate.value
})
const visibleSearched = computed(() => {
    if (!searchColumnInput.value) {
        return [ ...props.visibleColumns ]
    }
    return [ ...props.visibleColumns ].map(col => {
        if (col.label.fullTextLabel.toLowerCase().includes(searchColumnInput.value.toLowerCase())) {
            return {
                ...col,
                highlight: true
            }
        }
        return col
    })
})

const hiddenSearchedColumns = computed(() => {
    if (!searchColumnInput.value) {
        return []
    }
    return [ ...props.hiddenColumns ].filter((column) => column.label.fullTextLabel.toLowerCase().includes(searchColumnInput.value.toLowerCase()))
})

const hiddenUnSearchColumns = computed(() => {
    if (!searchColumnInput.value) {
        return [ ...props.hiddenColumns ]
    }
    return [ ...props.hiddenColumns ].filter((column) => !column.label.fullTextLabel.toLowerCase().includes(searchColumnInput.value.toLowerCase()))
})

//Methods
function selectAllColumns (state?: string | number | boolean) {
    if (state) {
        showAll()
    } else {
        hideAll()
    }
}

const addToHidden = (column: WidgetTemplateColumnWithConfig) => {
    updateLocalColumnToExport(column.prop, false)
}

const addToVisible = (column: WidgetTemplateColumnWithConfig) => {
    updateLocalColumnToExport(column.prop, true)
}

function updateLocalColumnToExport (columnProp: string, value: boolean) {
    const _data: ColumnsToExport = {}
    Object.keys(props.modelValue).forEach((key) => {
        _data[key] = { ...props.modelValue[key] }
    })
    if (!_data[columnProp]) {
        _data[columnProp] = {
            addToExport: value,
            sortOrder: Object.keys(_data).length
        }
    } else {
        _data[columnProp] = { ..._data[columnProp], addToExport: value }
    }
    emitUpdate(_data)
}

const onEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
    if (oldIndex === newIndex) return

    const _data: ColumnsToExport = {}
    Object.keys(props.modelValue).forEach((key) => {
        _data[key] = { ...props.modelValue[key] }
    })
    visibleDraggableColumns.value?.forEach((column: WidgetTemplateColumnWithConfig, i) => {
        _data[column.prop] = { ..._data[column.prop], sortOrder: i }
    })
    emitUpdate(_data)
}

const showAll = () => {
    const _data: ColumnsToExport = {}
    Object.keys(props.modelValue).forEach((key) => {
        _data[key] = { ...props.modelValue[key], addToExport: true }
    })
    emitUpdate(_data)
}

const hideAll = () => {
    const _data: ColumnsToExport = {}
    Object.keys(props.modelValue).forEach((key) => {
        _data[key] = { ...props.modelValue[key], addToExport: false }
    })
    emitUpdate(_data)
}

defineExpose({
    clearData: () => {
        searchColumnInput.value = ''
    }
})

watch(() => props.visibleColumns, (newVal) => {
    visibleDraggableColumns.value = [ ...newVal ]
}, { immediate: true })
</script>

<style scoped lang="scss">
.redesign-export-manage {
    .export-manage {
        max-height: 390px;
        overflow-y: scroll;
        background-color: var(--secondaryBgColor-color);
    }

    .column-item {
        @apply p-2 flex justify-between items-center mb-2;
        border: 1px solid var(--gray-150);
        border-radius: 4px;

        &--active {
            border: 1px solid var(--primary-actions);
        }
    }
}

</style>
